// This module is used both in App & Component contexts
// In Component context separate Hub is created to avoid interfering with Widget's Sentry
import * as Sentry from '@sentry/browser';
import { catchIfThrows, ignoreIfThrows, prependMessageToError } from '@wix/cashier-common/dist/src/utils/errorHandling';
import { isMonitoringEnabled } from './isMonitoringEnabled';

export let sentry = Sentry;

// In App context (iframe) initial window.Sentry is dummy object that subsequently is replaced with
// full-scale Sentry object (when first error occur Sentry bundle is loaded).
// Due to the way webpack bundles `@sentry/browser` (exports.module = Sentry) Sentry variable will
// always point to the dummy implementation, thus we need to manually update it.
// dummy Sentry implementation doesn't have this flag
if (!sentry.SDK_VERSION) {
  sentry.onLoad(() => {
    sentry = window.Sentry;
  });
}

let customSentryHub: Sentry.Hub;
let isInited = false;

const commonSentryConfig: Sentry.BrowserOptions = {
  blacklistUrls: [
    // /localhost/i,
    // /webpack-internal/i,
    // Extensions might inject content-scripts into the frame (bypassing our CSP by modifying CSP header on the flight)
    /extensions\//i,
    /^chrome:\/\//i,
  ],
  maxBreadcrumbs: 50,
  environment: process.env.NODE_ENV === 'production' ? 'prod' : 'dev',
  dsn: 'https://964c51bf38ca44d3a745de7bd112a92a@sentry.wixpress.com/408',
  // debug: true,
};

interface Extras {
  userId?: string;
  version?: string;
  [key: string]: string;
}

export const enrichErrorReports = ignoreIfThrows((extras: Extras = {}) => {
  if (!isInited) {
    return;
  }
  const { userId, version, ...tags } = extras;
  const client = customSentryHub || sentry;

  client.configureScope((scope) => {
    if (userId) {
      scope.setUser({ id: userId });
    }

    if (version) {
      scope.addEventProcessor(async (event) => ({
        ...event,
        release: version,
      }));
    }

    scope.setTags(tags);
  });
});

export const initAppErrorReporter = ignoreIfThrows((extras?: Extras) => {
  if (isInited) {
    return;
  }

  isInited = true;

  enrichErrorReports(extras);

  sentry.onLoad(() => {
    sentry.init(
      isMonitoringEnabled()
        ? commonSentryConfig
        : // in case of App context Sentry is already tracking errors, thus in order to disable it need to override beforeSend
          {
            beforeSend() {
              return null;
            },
          },
    );
  });
});

export const initComponentErrorReporter = ignoreIfThrows((extras?: Extras) => {
  if (isInited) {
    return;
  }

  isInited = true;

  if (!isMonitoringEnabled()) {
    return;
  }

  customSentryHub = new Sentry.Hub();
  // passing client to Hub constructor for some reason doesn't work
  customSentryHub.bindClient(
    new Sentry.BrowserClient({
      ...commonSentryConfig,
      beforeSend(event) {
        event.fingerprint = event.fingerprint || ['{{ error.type }}', '{{ error.value }}'];
        return event;
      },
      // if error reporter is used in "public" part of cc form (the one that lives inside the Widget's frame)
      // we don't want to catch all the errors on the page that renders Public component, thus we silence integrations
      // and capture exceptions by hand
      defaultIntegrations: [
        new Sentry.Integrations.InboundFilters(),
        new Sentry.Integrations.FunctionToString(),
        new Sentry.Integrations.UserAgent(),
      ],
    }),
  );

  enrichErrorReports(extras);
});

export const captureMessage = (message: string) => {
  if (isMonitoringEnabled() && isInited) {
    try {
      if (customSentryHub) {
        customSentryHub.run((currentHub) => {
          currentHub.captureMessage(message);
        });
      } else {
        sentry.captureMessage(message);
      }
    } catch (error) {
      console.error(`Failed to exec captureMessage`, error);
    }
  }

  console.error('Captured message:', message);
};

export const captureException = (error: Error, shouldRethrow = true, messagePrefix?: string) => {
  if (isMonitoringEnabled() && isInited) {
    try {
      let errorToReport: Error = error;

      if (messagePrefix) {
        errorToReport = prependMessageToError(error, messagePrefix);
      }

      if (customSentryHub) {
        customSentryHub.run((currentHub) => {
          currentHub.captureException(errorToReport);
        });
      } else {
        sentry.captureException(errorToReport);
      }
    } catch (e) {
      console.error(`Failed to exec captureException`, e);
    }
  }

  console.error('Captured exception:', error);

  if (shouldRethrow) {
    throw error;
  }
};

export const captureIfThrows: <T extends Function>(fn: T, messagePrefix?: string) => T = (fn, messagePrefix) =>
  catchIfThrows(fn, (error) => captureException(error, true, messagePrefix));
